import React from 'react';

import { SimpleObject } from '@hrlogics/helper-types';

import { useValidateSignupEmployeeDataLazyQuery } from 'src/api';
import { EmployeeBasicInfoFormValues } from 'src/types/signup';

import { useNotification } from '../app';

type EmployeeBasicInfoFormHandlerHookParams = {
  inviteId?: string;
  employeeSignupToken?: string | null;
  updateFormData: (data: SimpleObject) => void;
  setEmployeeSignupToken: (token: string | null) => void;
  goToNextStep: () => void;
};

export const useEmployeeBasicInfoFormHandler = ({
  inviteId,
  employeeSignupToken,
  updateFormData,
  setEmployeeSignupToken,
  goToNextStep,
}: EmployeeBasicInfoFormHandlerHookParams) => {
  const { createApolloContext, spawnNotification } = useNotification();

  const [validateSignupEmployeeData] = useValidateSignupEmployeeDataLazyQuery({
    context: createApolloContext(),
  });

  const handleEmployeeBasicInfoFormSubmit = React.useCallback(
    async (formValues: EmployeeBasicInfoFormValues) => {
      const { data: emailExistData } = await validateSignupEmployeeData({
        variables: {
          inviteId,
          employeeSignupToken: employeeSignupToken || undefined,
          employeeSsn: formValues.ssn,
          email: formValues.email,
        },
      });

      const employeeSignupTokenFromResponse =
        emailExistData?.validateSignupEmployeeData.employeeSignupToken;
      if (!employeeSignupTokenFromResponse) {
        return spawnNotification(
          'Something went wrong while validating your information. Please try again or contact support',
          'error',
        );
      }

      updateFormData(formValues);
      setEmployeeSignupToken(employeeSignupTokenFromResponse);
      return goToNextStep();
    },
    [
      validateSignupEmployeeData,
      updateFormData,
      inviteId,
      employeeSignupToken,
      spawnNotification,
      setEmployeeSignupToken,
      goToNextStep,
    ],
  );

  return { handleEmployeeBasicInfoFormSubmit };
};
